/**
 * PEStart — Read-only Bridge zum Coach-Planungsrechner (`planungsrechnerArchiv`).
 * STRIKT: Nur Coach-gesteuerte Szenarien aus dem Planungsrechner (God-Mode).
 * Keine Übernahme aus Mandanten-PEPlan (`pePlanArchiv`), keine Übernahme aus PEProfit, keine Alt-Fallbacks!
 */

import {
    calcAutoKundenVerlustProzent,
    createDefaultPEProfitPrognose,
    type PEProfitCalculations,
    type PEProfitPrognose,
    type PlanungsBasisDatenTyp,
    type PlanungsSzenario,
} from '../../types/peProfit';
import { buildPlanningBaselineFromLive } from '../../utils/planningBaselineSnapshot';
import {
    calculatePlanningScenario,
    calcAutoKundenSchwundPercent,
    createDefaultPlanningScenarioInputs,
    type PlanningBaselineData,
    type PlanningScenarioInputs,
    type PlanningScenarioOutputs,
} from '../../utils/planningEngine';
import type { PlanningSavedScenarioEntry } from '../../store/usePlanningSandboxStore';

export type AufbauHebelKategorie =
    | 'PREIS'
    | 'MATERIAL'
    | 'PERSONAL'
    | 'GEMEINKOSTEN'
    | 'INVESTITION'
    | 'DIENSTLEISTUNG'
    | 'SONSTIGE';

export type AufbauHebel = {
    id: string;
    kategorie: AufbauHebelKategorie;
    titel: string;
    detail: string;
    wirkungHinweis?: string;
};

export const PESTART_NO_AUFBAUPLAN_ID = '__NONE__';

export type PEStartAufbauSzenarioOption = {
    id: string;
    titel: string;
    timestamp: number;
    umsatzPrognoseMonat: number;
    umsatzDeltaMonat: number;
    ergebnisDeltaMonat: number;
    hebelCount: number;
};

export type PEStartAufbauPlanModel = {
    available: boolean;
    /** true, wenn Pläne im Planungsrechner existieren, aber bewusst „Ohne Aufbauplan“ gewählt wurde */
    disabledByUser?: boolean;
    sourceLabel: string;
    szenarioName: string;
    basisDatenTyp: PlanungsBasisDatenTyp;
    activeSzenarioId: string | null;
    archivCount: number;
    szenarioOptions: PEStartAufbauSzenarioOption[];
    hebel: AufbauHebel[];
    metrics: PEProfitCalculations | null;
    summary: {
        preisAnpassungProzent: number;
        kundenVerlustProzent: number;
        umsatzIstMonat: number;
        umsatzPrognoseMonat: number;
        umsatzDeltaMonat: number;
        ergebnisIstMonat: number;
        ergebnisPrognoseMonat: number;
        ergebnisDeltaMonat: number;
        rentabilitaetIst: number;
        rentabilitaetPrognose: number;
    } | null;
};

function n(v: unknown): number {
    const x = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(x) ? x : 0;
}

/** Legacy-Export (falls extern referenziert) */
export function selectPlanungsAnalysePrognose(state: any): {
    prognose: PEProfitPrognose;
    basisDatenTyp: PlanungsBasisDatenTyp;
    activeSzenarioId: string | null;
    archiv: PlanungsSzenario[];
} {
    const memory = state?.planungsAnalysePrognose;
    const fromClient = state?.clientData?.planungsAnalysePrognose;
    const prognose =
        (memory && typeof memory === 'object' ? memory : null) ||
        (fromClient && typeof fromClient === 'object' ? fromClient : null) ||
        createDefaultPEProfitPrognose();

    return {
        prognose,
        basisDatenTyp: 'start',
        activeSzenarioId: null,
        archiv: [],
    };
}

/** Legacy-Export für Prognose-Hebel */
export function buildAufbauHebelFromPrognose(prognose: PEProfitPrognose): AufbauHebel[] {
    const hebel: AufbauHebel[] = [];
    const preis = n(prognose.preisAnpassungProzent);
    if (preis !== 0) {
        const schwund =
            prognose.kundenVerlustProzent !== null && prognose.kundenVerlustProzent !== undefined
                ? n(prognose.kundenVerlustProzent)
                : calcAutoKundenVerlustProzent(preis);
        hebel.push({
            id: 'preis',
            kategorie: 'PREIS',
            titel: 'Preisoptimierung',
            detail: `Preisanpassung ${preis > 0 ? '+' : ''}${preis.toFixed(1)} %`,
            wirkungHinweis: `Kalkulierter Kundenschwund ${schwund.toFixed(1)} %`,
        });
    }
    return hebel;
}

/** Hebel-Liste aus einem Coach-Planungsrechner-Szenario (`PlanningScenarioInputs`). */
export function buildAufbauHebelFromPlanningScenario(
    inputs: PlanningScenarioInputs,
    baseline: PlanningBaselineData | null
): AufbauHebel[] {
    const hebel: AufbauHebel[] = [];

    const preis = n(inputs?.preisanpassung?.geplantePreiserhoehungPercent);
    if (Math.abs(preis) >= 0.01) {
        const schwund =
            inputs?.preisanpassung?.kundenSchwundPercent !== null &&
            inputs?.preisanpassung?.kundenSchwundPercent !== undefined
                ? n(inputs.preisanpassung.kundenSchwundPercent)
                : calcAutoKundenSchwundPercent(preis);
        hebel.push({
            id: 'preis',
            kategorie: 'PREIS',
            titel: 'Preisoptimierung',
            detail: `Preisanpassung ${preis > 0 ? '+' : ''}${preis.toFixed(1)} %`,
            wirkungHinweis: `Kalkulierter Kundenschwund ${schwund.toFixed(1)} %`,
        });
    }

    // Neuanstellungen (Zuwachs)
    for (const h of inputs?.zuwachs || []) {
        if (h?.aktiv === false) continue;
        const brutto = n(h.bruttoGehaltMonat);
        const wStd = n(h.wochenstunden);
        if (brutto <= 0 && wStd <= 0) continue;
        const rolleLabel =
            h.scope === 'azubi'
                ? 'Azubi'
                : h.scope === 'unproduktiv'
                  ? 'Unproduktiv / Assistenz'
                  : 'Fachkraft';
        hebel.push({
            id: `hire-${h.id}`,
            kategorie: 'PERSONAL',
            titel: `Personal-Zuwachs: ${h.bezeichnung || rolleLabel}`,
            detail: `${wStd.toLocaleString('de-DE')} Std./Wo. · ${brutto.toLocaleString('de-DE')} € Brutto/Monat`,
        });
    }

    // Bestands-Änderungen & Abgänge
    const empMap = new Map((baseline?.employees || []).map((e) => [e.id, e]));
    for (const dep of inputs?.abgaenge || []) {
        const ma = empMap.get(dep.employeeId);
        if (!ma) continue;
        if (dep.aktiv === true) {
            hebel.push({
                id: `dep-${ma.id}`,
                kategorie: 'PERSONAL',
                titel: `Personal-Abgang: ${ma.name}`,
                detail: `Abgang eingeplant · Auffangquote im Team: ${n(dep.auffangQuotePercent)} %`,
            });
            continue;
        }
        const effWStd = dep.customWochenstunden ?? ma.wochenstunden;
        const effBrutto = dep.customBruttoGehaltMonat ?? ma.grundgehaltBrutto;
        const effScope = dep.customScope || ma.scope;
        const hoursChanged = Math.abs(effWStd - ma.wochenstunden) >= 0.05;
        const salaryChanged = Math.abs(effBrutto - ma.grundgehaltBrutto) >= 0.5;
        const scopeChanged = effScope !== ma.scope;
        if (hoursChanged || salaryChanged || scopeChanged) {
            const deltaBrutto = effBrutto - ma.grundgehaltBrutto;
            hebel.push({
                id: `mod-${ma.id}`,
                kategorie: 'PERSONAL',
                titel: scopeChanged
                    ? `Personal-Übernahme / Rollenwechsel: ${ma.name}`
                    : `Personal-Anpassung: ${ma.name}`,
                detail: `IST ${ma.wochenstunden} Std. (${Math.round(ma.grundgehaltBrutto).toLocaleString('de-DE')} €) → ZIEL ${effWStd} Std. (${Math.round(effBrutto).toLocaleString('de-DE')} €, ${deltaBrutto >= 0 ? '+' : ''}${Math.round(deltaBrutto).toLocaleString('de-DE')} €)`,
            });
        }
    }

    // Inhaber-Anpassung
    if (baseline) {
        const baseWStd = n(baseline.inhaberProdWochenstunden);
        const neueWStd = n(inputs?.inhaber?.bedienzeitWochenstunden ?? baseWStd);
        const baseBedarf = n(baseline.inhaberEchterBedarfGesamt);
        const neuerBedarf = n(inputs?.inhaber?.kalkUnternehmerlohnMonat ?? baseBedarf);
        if (Math.abs(neueWStd - baseWStd) >= 0.05 || Math.abs(neuerBedarf - baseBedarf) >= 1) {
            hebel.push({
                id: 'inhaber',
                kategorie: 'PERSONAL',
                titel: 'Inhaber-Stuhlzeit & Unternehmerlohn',
                detail: `Stuhlzeit ${baseWStd} → ${neueWStd} Std./Wo. · Unternehmerlohn ${Math.round(baseBedarf).toLocaleString('de-DE')} € → ${Math.round(neuerBedarf).toLocaleString('de-DE')} €`,
            });
        }
    }

    // Zusatz-Material
    const zusatzMat = n(inputs?.investitionen?.zusatzMaterialMonatNetto);
    if (Math.abs(zusatzMat) >= 1) {
        const baseMat = n(baseline?.materialkostenMonatNetto);
        const zielMat = Math.max(0, baseMat + zusatzMat);
        hebel.push({
            id: 'material-ziel',
            kategorie: 'MATERIAL',
            titel: 'Material / Wareneinsatz',
            detail: `IST ${Math.round(baseMat).toLocaleString('de-DE')} € → ZIEL ${Math.round(zielMat).toLocaleString('de-DE')} € (${zusatzMat >= 0 ? '+' : ''}${Math.round(zusatzMat).toLocaleString('de-DE')} € / Monat)`,
            wirkungHinweis: inputs?.investitionen?.materialNotiz?.trim() || undefined,
        });
    }

    // BWA-Kostenarten
    for (const b of inputs?.investitionen?.bwaAnpassungen || []) {
        if (!b?.aktiv) continue;
        const delta = n(b.zielMonatNetto) - n(b.istMonatNetto);
        hebel.push({
            id: `gk-${b.id}`,
            kategorie: 'GEMEINKOSTEN',
            titel: `Betriebskosten: ${b.label || b.bwaKey}`,
            detail: `IST ${Math.round(n(b.istMonatNetto)).toLocaleString('de-DE')} € → ZIEL ${Math.round(n(b.zielMonatNetto)).toLocaleString('de-DE')} € (${delta >= 0 ? '+' : ''}${Math.round(delta).toLocaleString('de-DE')} €)`,
            wirkungHinweis: b.notiz?.trim() || undefined,
        });
    }

    // Einzel-Investitionen
    for (const inv of inputs?.investitionen?.einzelInvestitionen || []) {
        if (!inv?.aktiv) continue;
        hebel.push({
            id: `inv-${inv.id}`,
            kategorie: 'INVESTITION',
            titel: `Investition: ${inv.label || 'Position'}`,
            detail: `${Math.round(n(inv.monatsrateNetto)).toLocaleString('de-DE')} € / Monat netto`,
        });
    }

    // Neue Dienstleistungen
    for (const dl of inputs?.investitionen?.neueDienstleistungen || []) {
        if (!dl?.aktiv) continue;
        hebel.push({
            id: `dl-${dl.id}`,
            kategorie: 'DIENSTLEISTUNG',
            titel: `Neue Leistung: ${dl.label || 'Dienstleistung'}`,
            detail: `+${Math.round(n(dl.zusatzUmsatzMonatBrutto)).toLocaleString('de-DE')} € Brutto/Monat`,
        });
    }

    return hebel;
}

/**
 * Volle Aufbau-Plan-Ansicht:
 * STRIKT NUR aus dem Coach-Planungsrechner (`planungsrechnerArchiv`).
 * Kein PEPlan, kein PEProfit, keine Alt-Fallbacks — wenn im Planungsrechner nichts gespeichert ist, ist hier leer!
 */
export function computePEStartAufbauPlan(
    appState: any,
    calc: any,
    clientData: any,
    settings: any,
    preferredSzenarioId?: string | null
): PEStartAufbauPlanModel {
    const cd = clientData || appState?.clientData || {};
    const planungsrechnerArchiv: PlanningSavedScenarioEntry[] = Array.isArray(
        cd?.planungsrechnerArchiv
    )
        ? cd.planungsrechnerArchiv
        : [];

    const sortedPlanungsrechnerArchiv = [...planungsrechnerArchiv].sort(
        (a, b) => (b?.timestamp || 0) - (a?.timestamp || 0)
    );

    if (sortedPlanungsrechnerArchiv.length === 0 || !calc?.ready) {
        return {
            available: false,
            disabledByUser: false,
            sourceLabel: 'Planungsrechner (Coach)',
            szenarioName: '—',
            basisDatenTyp: 'ist',
            activeSzenarioId: null,
            archivCount: sortedPlanungsrechnerArchiv.length,
            szenarioOptions: [],
            hebel: [],
            metrics: null,
            summary: null,
        };
    }

    const { baseline, globalSettings } = buildPlanningBaselineFromLive(
        cd,
        calc,
        settings
    );
    const defaultInputs = createDefaultPlanningScenarioInputs(baseline);

    const evaluateEntry = (entry: PlanningSavedScenarioEntry) => {
        const mergedInputs: PlanningScenarioInputs = {
            ...defaultInputs,
            ...(entry.inputs || {}),
            szenarioTitel:
                entry.titel ||
                entry.inputs?.szenarioTitel ||
                'Planungs-Szenario',
            inhaber: {
                ...defaultInputs.inhaber,
                ...(entry.inputs?.inhaber || {}),
            },
            investitionen: {
                ...defaultInputs.investitionen,
                ...(entry.inputs?.investitionen || {}),
            },
            preisanpassung: {
                ...defaultInputs.preisanpassung,
                ...(entry.inputs?.preisanpassung || {}),
            },
        };
        const outputs: PlanningScenarioOutputs = calculatePlanningScenario(
            baseline,
            mergedInputs,
            globalSettings
        );
        const hebel = buildAufbauHebelFromPlanningScenario(mergedInputs, baseline);
        const istU = n(outputs.vergleich.monatsumsatzNetto.vorher);
        const progU = n(outputs.vergleich.monatsumsatzNetto.nachher);
        const istE = n(outputs.vergleich.ueberschussMonatNetto.vorher);
        const progE = n(outputs.vergleich.ueberschussMonatNetto.nachher);
        return {
            entry,
            mergedInputs,
            outputs,
            hebel,
            istU,
            progU,
            istE,
            progE,
        };
    };

    const evaluatedAll = sortedPlanungsrechnerArchiv.map(evaluateEntry);

    const szenarioOptions: PEStartAufbauSzenarioOption[] = evaluatedAll.map((ev) => ({
        id: ev.entry.id,
        titel: ev.entry.titel || ev.mergedInputs.szenarioTitel || 'Planungs-Szenario',
        timestamp: ev.entry.timestamp || 0,
        umsatzPrognoseMonat: ev.progU,
        umsatzDeltaMonat: ev.progU - ev.istU,
        ergebnisDeltaMonat: ev.progE - ev.istE,
        hebelCount: ev.hebel.length,
    }));

    const selectedScenarioId =
        preferredSzenarioId !== undefined && preferredSzenarioId !== null
            ? preferredSzenarioId
            : cd?.peStartSelectedPlanungsrechnerId || null;

    if (selectedScenarioId === PESTART_NO_AUFBAUPLAN_ID) {
        return {
            available: false,
            disabledByUser: true,
            sourceLabel: 'Planungsrechner (Coach)',
            szenarioName: 'Ohne Aufbauplan',
            basisDatenTyp: 'ist',
            activeSzenarioId: PESTART_NO_AUFBAUPLAN_ID,
            archivCount: sortedPlanungsrechnerArchiv.length,
            szenarioOptions,
            hebel: [],
            metrics: null,
            summary: null,
        };
    }

    const chosen =
        (selectedScenarioId
            ? evaluatedAll.find((ev) => ev.entry.id === selectedScenarioId)
            : null) || evaluatedAll[0];

    const { entry: latestEntry, mergedInputs, outputs, hebel, istU, progU, istE, progE } = chosen;
    const rentIst = n(outputs.vergleich.rentabilitaetPercent.vorher);
    const rentProg = n(outputs.vergleich.rentabilitaetPercent.nachher);
    const preisPct = n(mergedInputs.preisanpassung?.geplantePreiserhoehungPercent);
    const schwundPct =
        mergedInputs.preisanpassung?.kundenSchwundPercent !== null &&
        mergedInputs.preisanpassung?.kundenSchwundPercent !== undefined
            ? n(mergedInputs.preisanpassung.kundenSchwundPercent)
            : calcAutoKundenSchwundPercent(preisPct);

    return {
        available: true,
        sourceLabel: 'Planungsrechner (Coach)',
        szenarioName: latestEntry.titel || mergedInputs.szenarioTitel || 'Planungs-Szenario',
        basisDatenTyp: 'ist',
        activeSzenarioId: latestEntry.id,
        archivCount: sortedPlanungsrechnerArchiv.length,
        szenarioOptions,
        hebel,
        metrics: null,
        summary: {
            preisAnpassungProzent: preisPct,
            kundenVerlustProzent: schwundPct,
            umsatzIstMonat: istU,
            umsatzPrognoseMonat: progU,
            umsatzDeltaMonat: progU - istU,
            ergebnisIstMonat: istE,
            ergebnisPrognoseMonat: progE,
            ergebnisDeltaMonat: progE - istE,
            rentabilitaetIst: rentIst,
            rentabilitaetPrognose: rentProg,
        },
    };
}
